import classNames from 'classnames';
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

import { Photo, WithClassName } from '@types';
import useVisibleOnScreen from '@utils/useVisibleOnScreen';
import { PhotoMeta } from './meta';

interface ImageProps {
  active?: boolean;
  label?: ReactNode;
  labelSub?: ReactNode;
  labelDetails?: ReactNode;
  photo: Photo;
  thumbnail?: boolean;
}

const Image = ({
  active,
  className = 'w-full aspect-square',
  label,
  labelSub,
  labelDetails,
  photo,
  thumbnail,
}: ImageProps & WithClassName) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isVisible } = useVisibleOnScreen(ref);

  const [isLoading, setIsLoading] = useState(false);
  const [imgUrl, setImgUrl] = useState<string>();

  const imgStyle: CSSProperties = imgUrl
    ? {
        backgroundImage: `url(${imgUrl})`,
        backgroundPosition: photo.coverPosition || 'center',
        backgroundSize: photo.coverSize,
      }
    : {};

  const overlayClassNames = classNames(
    'absolute top-0 right-0 bottom-0 left-0',
    !active && 'opacity-0 group-hover:opacity-100 transition-opacity',
  );

  const getImg = async () => {
    setIsLoading(true);
    const url = `/img/${thumbnail ? `thumbs/` : ''}${photo.id}.jpg`;

    try {
      const response = await fetch(url);
      const file = await response.blob();
      setImgUrl(URL.createObjectURL(file));
    } catch {}

    setIsLoading(false);
  };

  useEffect(() => {
    if (isVisible && !imgUrl) {
      getImg();
    }
  }, [isVisible]);

  return (
    <div
      ref={ref}
      className={classNames('group', className)}
      itemScope
      itemType="https://schema.org/Photograph"
    >
      <div
        className={classNames(
          'relative w-full h-full flex items-center justify-center bg-darker border shadow-lg',
          // isVisible ? 'border-red-500' : 'border-darker',
          active
            ? 'border-accent'
            : 'border-darker group-hover:border-accent transition-colors',
        )}
      >
        {/* Meta */}
        <PhotoMeta photo={photo} />

        {!isLoading && imgUrl ? (
          <>
            {/* Image */}
            <div
              className="w-full h-full bg-cover bg-no-repeat bg-[black]"
              style={imgStyle}
            />
            {/* Grayscale hover overlay */}
            {photo.color && (
              <div
                className={classNames(
                  overlayClassNames,
                  'bg-cover bg-no-repeat grayscale',
                )}
                style={imgStyle}
              />
            )}
            {/* Yellow hover overlay */}
            <div
              className={classNames(
                overlayClassNames,
                'bg-accent mix-blend-color-burn',
              )}
            />
          </>
        ) : null}

        {/* Center lines */}
        {/* <div className="absolute top-0 right-0 bottom-0 left-0 border border-white opacity-75" />
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white opacity-75" />
        <div className="absolute left-0 right-0 top-1/2 h-px bg-white opacity-75" /> */}
      </div>
      {(label || labelSub) && (
        <div className="mt-2 text-center">
          {label && (
            <div className="font-title text-xl lowercase text-accent">
              {label}
            </div>
          )}
          {labelSub && <div className="mt-1 text-xs lowercase">{labelSub}</div>}
          {labelDetails && (
            <div className="mt-1 text-xs italic">{labelDetails}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Image;
